export const OBJECTIVE_UNIT_SUGGESTIONS = ['$', '€', '£', '%', 'kg', 'km', 'h', 'euros'];

export const DURATION_FORMAT = 'duration';

export type ObjectiveQuantityFormat = typeof DURATION_FORMAT;

export interface UnitQuantity {
  format?: ObjectiveQuantityFormat | null;
  unit?: string | null;
}

export const isDurationFormat = (quantity?: UnitQuantity | null) => (
  quantity?.format === DURATION_FORMAT
);

export const objectiveUnit = (quantity?: UnitQuantity | null) => {
  if (isDurationFormat(quantity)) {
    return null;
  }
  const unit = typeof quantity?.unit === 'string' ? quantity.unit.trim() : '';
  return unit || null;
};

const pad = (value: number) => String(value).padStart(2, '0');

export const formatDuration = (value: number) => {
  if (!Number.isFinite(value)) {
    return String(value);
  }
  const sign = value < 0 ? '-' : '';
  const totalMs = Math.round(Math.abs(value) * 1000);
  const hours = Math.floor(totalMs / 3_600_000);
  const minutes = Math.floor((totalMs % 3_600_000) / 60_000);
  const milliseconds = totalMs % 60_000;
  const wholeSeconds = Math.floor(milliseconds / 1000);
  const fractionMs = milliseconds % 1000;
  const seconds = fractionMs === 0
    ? pad(wholeSeconds)
    : `${pad(wholeSeconds)}.${String(fractionMs).padStart(3, '0').replace(/0+$/, '')}`;
  if (hours > 0) {
    return `${sign}${hours}:${pad(minutes)}:${seconds}`;
  }
  return `${sign}${minutes}:${seconds}`;
};

const INTEGER_PART = /^\d+$/;
const DECIMAL_PART = /^\d+(?:\.\d+)?$/;

export const parseDuration = (input: string): number | null => {
  const trimmed = input.trim();
  if (!trimmed) {
    return null;
  }
  const negative = trimmed.startsWith('-');
  const body = (negative ? trimmed.slice(1) : trimmed).trim();
  if (!body) {
    return null;
  }
  const parts = body.split(':');
  if (parts.length > 3 || parts.some(part => part.length === 0)) {
    return null;
  }
  const leading = parts.slice(0, -1);
  const last = parts[parts.length - 1];
  if (leading.some(part => !INTEGER_PART.test(part)) || !DECIMAL_PART.test(last)) {
    return null;
  }
  const numbers = parts.map(Number);
  const seconds = numbers[numbers.length - 1];
  const minutes = numbers.length > 1 ? numbers[numbers.length - 2] : 0;
  const hours = numbers.length === 3 ? numbers[0] : 0;
  if (numbers.length > 1 && (minutes >= 60 || seconds >= 60)) {
    return null;
  }
  const total = hours * 3600 + minutes * 60 + seconds;
  return negative ? -total : total;
};

export const formatObjectiveQuantity = (
  value: number | string,
  quantity?: UnitQuantity | null,
) => {
  if (typeof value === 'number' && isDurationFormat(quantity)) {
    return formatDuration(Object.is(value, -0) ? 0 : value);
  }
  const text = typeof value === 'number'
    ? String(Object.is(value, -0) ? 0 : value)
    : value;
  const unit = objectiveUnit(quantity);
  if (!unit) {
    return text;
  }
  if (unit === '%') {
    return `${text}%`;
  }
  return `${text} ${unit}`;
};

export const objectiveAxisPad = (quantity?: UnitQuantity | null) => {
  const sample = isDurationFormat(quantity)
    ? formatDuration(360000)
    : objectiveUnit(quantity)
      ? formatObjectiveQuantity('10k', quantity)
      : '';
  if (!sample) {
    return undefined;
  }
  return Math.min(140, Math.max(isDurationFormat(quantity) ? 72 : 48, 20 + sample.length * 7));
};
